const DANGEROUS_KEYS = new Set(['__proto__', 'constructor', 'prototype'])

export function isDangerousKey(key: string): boolean {
  return DANGEROUS_KEYS.has(key)
}

/** Copy a key without invoking the `__proto__` setter. */
export function assignJsonKey(
  target: Record<string, unknown>,
  key: string,
  value: unknown
): void {
  if (!isDangerousKey(key)) {
    target[key] = value
    return
  }

  Object.defineProperty(target, key, {
    value,
    writable: true,
    enumerable: true,
    configurable: true,
  })
}

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false
  }

  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

export function cloneJson(value: unknown): unknown {
  if (typeof value !== 'object' || value === null) {
    return value
  }

  return structuredClone(value)
}

export function deepFreeze<T>(value: T): T {
  if (typeof value !== 'object' || value === null || Object.isFrozen(value)) {
    return value
  }

  for (const key of Object.keys(value as object)) {
    deepFreeze((value as Record<string, unknown>)[key])
  }

  return Object.freeze(value)
}

export function jsonEqual(left: unknown, right: unknown): boolean {
  if (left === right) {
    return true
  }

  if (typeof left !== 'object' || typeof right !== 'object' || left === null || right === null) {
    return false
  }

  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
      return false
    }

    return left.every((item, index) => jsonEqual(item, right[index]))
  }

  const leftRecord = left as Record<string, unknown>
  const rightRecord = right as Record<string, unknown>
  const leftKeys = Object.keys(leftRecord)
  const rightKeys = Object.keys(rightRecord)

  if (leftKeys.length !== rightKeys.length) {
    return false
  }

  return leftKeys.every(key => jsonEqual(leftRecord[key], rightRecord[key]))
}

export function valueAt(source: unknown, path: string): unknown {
  if (!path || path === '$') {
    return source
  }

  let current = source
  for (const part of path.split('.')) {
    if (typeof current !== 'object' || current === null) {
      return undefined
    }

    current = (current as Record<string, unknown>)[part]
  }

  return current
}
